import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { registerConfirmImpl } from "./confirm";
function SvgCodeDisplay({ svgContent }) {
    const problemElements = new Set(['clippath', 'defs']);
    const escapeHtml = (s) => s.replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    const formatLines = (raw) => {
        return raw
            .replace(/>[\t ]*</g, '><')
            .replace(/></g, '>\n<')
            .split(/\n+/)
            .map(l => l.trim())
            .filter(Boolean);
    };
    const highlightTag = (rawTag) => {
        if (/^<\?xml/i.test(rawTag) || /^<!--/.test(rawTag)) {
            return `<span class="svg-comment">${escapeHtml(rawTag)}</span>`;
        }
        const isClosing = /^<\//.test(rawTag);
        const selfClosing = /\/>$/.test(rawTag);
        const inner = rawTag.slice(1, rawTag.length - (selfClosing ? 2 : 1)).trim().replace(/^\//, '').trim();
        const parts = inner.match(/([^\s"']+)|("[^"]*"|'[^']*')/g) || [];
        if (!parts.length)
            return escapeHtml(rawTag);
        const tagName = parts[0];
        const lowerTag = tagName.toLowerCase();
        const attrs = parts.slice(1);
        let out = '&lt;';
        if (isClosing)
            out += '/';
        out += `<span class="svg-tag">${escapeHtml(tagName)}</span>`;
        for (const attr of attrs) {
            if (!/=/.test(attr)) {
                out += ` <span class="svg-attribute-name">${escapeHtml(attr)}</span>`;
                continue;
            }
            const eqIdx = attr.indexOf('=');
            const name = attr.slice(0, eqIdx);
            let value = attr.slice(eqIdx + 1);
            const quote = value[0];
            if ((quote === '"' || quote === "'") && value[value.length - 1] === quote) {
                value = value.slice(1, -1);
                out += ` <span class="svg-attribute-name">${escapeHtml(name)}</span>=<span class="svg-attribute-value">${quote}<span class="svg-string">${escapeHtml(value)}</span>${quote}</span>`;
            }
            else {
                out += ` <span class="svg-attribute-name">${escapeHtml(name)}</span>=<span class="svg-attribute-value"><span class="svg-string">${escapeHtml(value)}</span></span>`;
            }
        }
        if (selfClosing)
            out += '/';
        out += '&gt;';
        if (problemElements.has(lowerTag)) {
            out = `<span class="svg-error-tag">${out.replace(new RegExp(tagName, 'g'), `<span class=\"svg-highlight-error\">${escapeHtml(tagName)}</span>`)}</span>`;
        }
        return out;
    };
    const buildHighlighted = (svg) => {
        const lines = formatLines(svg);
        let indent = 0;
        const result = [];
        for (let rawLine of lines) {
            const trimmed = rawLine.trim();
            let content = '';
            if (trimmed.startsWith('<')) {
                const isClosing = /^<\//.test(trimmed);
                if (isClosing)
                    indent = Math.max(0, indent - 1);
                content = highlightTag(trimmed);
                if (!isClosing && !/\/?>$/.test(trimmed))
                    indent++;
            }
            else {
                content = escapeHtml(trimmed);
            }
            result.push('  '.repeat(indent - (trimmed.startsWith('</') ? 0 : 0)) + content);
        }
        return result.join('\n');
    };
    const highlightedSvg = buildHighlighted(svgContent);
    return (_jsx("div", { className: "confirm-svg-container", children: _jsx("pre", { className: "svg-code", dangerouslySetInnerHTML: { __html: highlightedSvg } }) }));
}
export function ConfirmHost({ children }) {
    const [state, setState] = useState(null);
    useEffect(() => {
        registerConfirmImpl((opts) => new Promise((resolve) => setState(Object.assign(Object.assign({}, opts), { resolve }))));
        return () => registerConfirmImpl(() => Promise.resolve(false));
    }, []);
    return (_jsxs(_Fragment, { children: [children, state && (_jsx("div", { className: "confirm-overlay", children: _jsxs("div", { className: "confirm-dialog", children: [state.title && _jsx("h3", { className: "confirm-title", children: state.title }), _jsx("p", { className: "confirm-message", children: state.message }), state.svgContent && _jsx(SvgCodeDisplay, { svgContent: state.svgContent }), _jsxs("div", { className: "confirm-buttons", children: [_jsx("button", { className: "confirm-button confirm-button--cancel", onClick: () => {
                                        state.resolve(false);
                                        setState(null);
                                    }, children: state.cancelText }), _jsx("button", { className: `confirm-button ${state.danger ? "confirm-button--danger" : "confirm-button--primary"}`, onClick: () => {
                                        state.resolve(true);
                                        setState(null);
                                    }, children: state.okText })] })] }) }))] }));
}
